/* eslint-disable max-params */
import React, {FC, useRef, useState} from "react";
import {ColumnProps} from "antd/lib/table";
import {InputRef} from "antd";
import css from "../../views/TableColumnRenderers/TableColumnRenderers.less";
import {
    ArrayDataOptions,
    GetEmailNotificationsQuery,
    Maybe,
} from "../../generated/graphql";
import {EyeOutlined} from "@ant-design/icons";
import {
    renderUnknown,
    renderTimeFromNow,
    renderContinuousText,
    renderTemplateType,
    renderTextStandard,
    RenderReceiverName,
    renderMessageStatus,
    renderAttachments,
    renderBookingNumber,
    RenderBookingNumber,
} from "../../views/TableColumnRenderers/TableColumnRenderers";
import i18n from "../../services/i18n";
import {ISODateString} from "../../helpers/typeScriptHelpers";
import columnSortFilterTest, {
    AutoCompleteOptions,
    InputType,
} from "../../helpers/columnSortFilterTest";
import {mailjetStatusOptions} from "./emaiNotificationStatusText";

type ExtraColumns = {
    // fullname: "fullname";
    // age: "age";
    bookingNumber: "bookingNumber";
    createDate: "createDate";
    actions: "actions";
};

const templateNameAutoCompleteOptions: AutoCompleteOptions = [
    {label: "Rechnung", value: "Rechnung"},
    {label: "Buchungsbestätigung", value: "Buchungsbestätigung"},
    {label: "Stornierung", value: "Stornierung"},
    {label: "Wartelistenbestätigung", value: "Wartelistenbestätigung"},
    {label: "Interessenten-Bestätigung", value: "Interessenten-Bestätigung"},
    {label: "Storno-Rechnung", value: "Storno-Rechnung"},
    {label: "Kurserinnerung", value: "Kurserinnerung"},
    {label: "Mahnung", value: "Mahnung"},
    // Mollie-Zahlungslink-Mails: value = technischer Key (so in der DB als
    // templateName gespeichert), damit der ILIKE-Filter greift; label = lesbarer Name.
    {label: "Zahlungslink (Mollie)", value: "paymentLinkDispatch"},
    {label: "Sammel-Zahlungslink (Mollie)", value: "paymentLinkBundleDispatch"},
];

// Technische Template-Keys, die System-Mails (z.B. Mollie-Zahlungslinks) direkt als
// templateName speichern statt eines lesbaren Namens. In der Anzeige werden sie über
// renderTemplateType in einen lesbaren Namen übersetzt — wirkt auch rückwirkend auf
// bereits versendete Mails.
const translatableTemplateNameKeys = [
    "paymentLinkDispatch",
    "paymentLinkBundleDispatch",
    "paymentTransferDetailsDispatch",
];

const shownMailjetStatusOptions = [
    "unknown",
    "queued",
    "sent",
    "opened",
    "clicked",
    "bounce",
    "spam",
    "blocked",
    "hardbounced",
    "softbounced",
];

export type TableType = GetEmailNotificationsQuery["emailNotifications"]["items"][0] &
    ExtraColumns;

export const useHandleColumns = (
    columnsToShow: Array<keyof TableType>,
    options: Maybe<ArrayDataOptions> | undefined,
    setOptions: (options: ArrayDataOptions) => void,
    activeFilters?: ArrayDataOptions["filter"],
    hideSortFilter?: boolean,
    onViewDetail?: (id: string) => void,
) => {
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const shownColumns: Array<ColumnProps<TableType>> = [];

    const getColumnsToShow = () => {
        columns.forEach((col) => {
            if (columnsToShow.includes(col.dataIndex)) {
                shownColumns.push(col);
            }
        });
        moveActionColumnToLastPosition(shownColumns);
    };

    const moveActionColumnToLastPosition = (
        shownColumns: Array<ColumnProps<TableType>>,
    ) => {
        shownColumns.forEach((col) => {
            if (col.fixed === "right") {
                shownColumns.push(
                    shownColumns.splice(shownColumns.indexOf(col), 1)[0],
                );
            }
        });
    };

    const columns: Array<ColumnProps<TableType>> = [];

    const column: ColumnProps<TableType> = {
        title: "",
        fixed: false,
    };

    const inputRef = useRef<InputRef>(null);

    const ref = inputRef.current?.focus({
        cursor: "all",
    });

    columnsToShow.forEach((col: keyof TableType) => {
        const c = {...column};
        const colTitle = i18n.views.TableColumnTitles[col]();

        c.title = i18n.views.TableColumnTitles[col]();
        c.dataIndex = col;
        c.ellipsis = true;
        c.width = 100 + colTitle.length * 3;

        const path = c.dataIndex;

        const sortFilter = ({
            dataIndex,
            disabled,
            type,
            selectOptions,
            activeFilters,
            autoCompleteOptions,
        }: {
            dataIndex: string;
            disabled?: boolean;
            type: InputType;
            selectOptions?: Array<{
                label: string;
                value: string;
            }>;
            activeFilters?: ArrayDataOptions["filter"] | undefined;
            autoCompleteOptions?: AutoCompleteOptions;
        }) =>
            // eslint-disable-next-line max-params
            {
                // if (hideFiltersAndSorters) {
                //     return null;
                // }

                Object.entries(
                    columnSortFilterTest({
                        dataIndex,
                        type,
                        selectOptions,
                        options,
                        setOptions,
                        dropdownOpen,
                        setDropdownOpen,
                        autoCompleteOptions,
                        table: "sentMail", // FIX: Korrekte Tabelle für SentMails
                    }),
                ).map(([key, value]) => {
                    if (key !== "filteredValue") {
                        // @ts-ignore
                        return (c[key] = disabled ? null : value);
                    }

                    return null;
                });
            };

        switch (col) {
            case "actions":
                c.fixed = "right";
                c.width = 50;
                c.align = "center";
                c.render = (text: unknown, record: TableType) => (
                    <EyeOutlined
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onViewDetail) {
                                onViewDetail(record.id);
                            }
                        }}
                        style={{cursor: "pointer", fontSize: 16}}
                    />
                );
                break;
            case "createDate":
                c.title = i18n.views.TableColumnTitles.sentAt();
                c.render = (text: ISODateString, record: TableType) =>
                    renderTimeFromNow({date: record.createDate, short: true});

                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;

            case "status":
                c.render = (text: string, record: TableType) =>
                    renderMessageStatus(record.messageStatus);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "select",
                    // selectOptions: mailjetStatusOptions,
                    selectOptions: mailjetStatusOptions.filter((option) =>
                        shownMailjetStatusOptions.includes(option.value),
                    ),
                });
                c.sorter = false;
                break;

            case "text":
                c.render = (text: string, record: TableType) =>
                    renderContinuousText(record.text);
                break;
            case "templateType":
                c.render = (text: string, record: TableType) =>
                    renderTemplateType({templateType: record.templateType});
                break;
            case "templateName":
                c.render = (text: string, record: TableType) => {
                    // System-Mails (z.B. Mollie-Zahlungslinks) speichern den
                    // technischen Typ-Key als templateName → über renderTemplateType
                    // in einen lesbaren Namen übersetzen.
                    if (
                        record.templateName &&
                        translatableTemplateNameKeys.includes(record.templateName)
                    ) {
                        return renderTemplateType({
                            templateType: record.templateName,
                        });
                    }

                    return record.templateName
                        ? renderTextStandard(record.templateName)
                        : renderTemplateType({
                              templateType: record.templateType,
                          });
                };

                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "autoComplete",
                    autoCompleteOptions: templateNameAutoCompleteOptions,
                });
                // sortFilter(
                //     c.dataIndex,
                //     path,
                //     c,
                //     "enabled",
                //     "autoComplete",
                //     undefined,
                //     activeFilters,
                //     templateNameAutoCompleteOptions,
                // );
                break;
            case "email":
                c.render = (text: string, record: TableType) =>
                    renderTextStandard(record.email);
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "receiverName":
                c.render = (text: string, record: TableType) => ({
                    displayName: "receiverName",
                    children: (
                        <RenderReceiverName
                            receiverName={record.receiverName ?? undefined}
                            bookerId={record.bookerId ?? ""}
                            email={record.email}
                        />
                    ),
                });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "subject":
                c.render = (text: string, record: TableType) =>
                    renderTextStandard(record.subject ?? "");
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: false,
                    type: "search",
                });
                break;
            case "bookingNumber":
                c.render = (text: string, record: TableType) =>
                    record.booking
                        ? renderBookingNumber({
                              bookingNumberPrefix:
                                  record.booking.bookingNumberPrefix,
                              bookingNumber: record.booking.bookingNumber,
                              bookingType: record.booking.bookingType,
                              className: css.primary,
                              canceled: Boolean(
                                  record.booking?.canceled !== null,
                              ),
                              bookingId: record.booking.id,
                          })
                        : null;
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                break;
            case "attachments":
                c.width = 50;
                c.render = (text: string, record: TableType) =>
                    renderAttachments({
                        attachments: record.attachments as Array<{
                            contentType?: "string";
                            fileName?: "string";
                        }>,
                    });
                sortFilter({
                    dataIndex: c.dataIndex,
                    disabled: true,
                    type: "search",
                });
                break;
            case "bookingId":
                c.title = i18n.views.TableColumnTitles.linkedBooking();
                c.align = "center";
                c.render = (text: string, record: TableType) => ({
                    displayName: "bookingNumber",
                    children: (
                        <RenderBookingNumber
                            bookingNumberPrefix={
                                record.booking?.bookingNumberPrefix
                            }
                            bookingNumber={record.booking?.bookingNumber}
                            bookingType={record.booking?.bookingType}
                            canceled={record.booking?.canceled}
                            bookingId={record.booking?.id}
                            invoiceId={record.booking?.invoice?.id ?? undefined}
                            reversalInvoiceId={
                                record.booking?.reversalInvoice?.id ?? undefined
                            }
                            bookingDeleted={record.bookingDeleted}
                        />
                    ),
                });
                break;
            default:
                c.fixed = false;
                c.render = (text) => renderUnknown(text);
                // sortFilter(c.dataIndex, path, c, "disabled");
                break;
        }
        columns.push(c);
    });

    getColumnsToShow();

    return shownColumns;
};
